import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { useAppShell } from '../AppShellContext';
import { SidebarBrand } from './SidebarBrand';
import { SidebarNav } from './SidebarNav';
import { ViewModeDropup } from './ViewModeDropup';

import type { SidebarProps } from './Sidebar.types';

import { LAYOUT_DIMENSIONS } from '@/types/layout';

/**
 * Sidebar navigation component for tablet and desktop layouts.
 *
 * Features:
 * - Fixed width (240px expanded, 72px collapsed)
 * - Smooth expand/collapse animation
 * - Navigation items with icons, labels, badges
 * - Nested navigation support
 * - Section dividers
 * - Role-based navigation (client vs trainer)
 * - Collapsed state shows icons with tooltips
 *
 * @example
 * ```tsx
 * <Sidebar
 *   items={[
 *     { id: 'dashboard', label: 'Dashboard', icon: <HomeIcon />, href: '/' },
 *     { type: 'divider', id: 'div-1' },
 *     { id: 'settings', label: 'Settings', icon: <CogIcon />, href: '/settings' },
 *   ]}
 *   activeItemId="dashboard"
 *   onItemClick={(item) => navigate(item.href)}
 * />
 * ```
 */
export function Sidebar({
  items,
  activeItemId,
  onItemClick,
  collapsed: controlledCollapsed,
  userRole,
  brand,
  className = '',
  showViewMode = false,
  viewMode,
  onViewModeChange,
}: SidebarProps): React.JSX.Element {
  const { sidebarExpanded, toggleSidebar, sidebarBehavior } = useAppShell();

  // Use controlled collapsed state if provided, otherwise derive from context
  const isCollapsed =
    controlledCollapsed !== undefined ? controlledCollapsed : !sidebarExpanded;

  // Only show expand/collapse button when sidebar behavior allows expansion
  const canExpandSidebar = sidebarBehavior === 'expandable';

  // Calculate width
  const width = isCollapsed
    ? LAYOUT_DIMENSIONS.sidebarWidthCollapsed
    : LAYOUT_DIMENSIONS.sidebarWidthExpanded;

  return (
    <div
      className={`
        sidebar flex h-full flex-col
        ${className}
      `}
      style={{ width }}
      role="complementary"
      aria-label="Sidebar"
      data-collapsed={isCollapsed}
      data-role={userRole}
    >
      {/* Brand header section */}
      {brand && <SidebarBrand brand={brand} collapsed={isCollapsed} />}

      {/* Navigation items */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4">
        <SidebarNav
          items={items}
          activeItemId={activeItemId || null}
          onItemClick={onItemClick!}
          collapsed={isCollapsed}
        />
      </div>

      {/* View mode dropup (dashboard only) */}
      {showViewMode && (
        <div className="border-t border-stroke-default px-3 py-3">
          <ViewModeDropup
            value={viewMode}
            onChange={onViewModeChange ?? (() => {})}
            collapsed={isCollapsed}
            userRole={userRole ?? 'client'}
          />
        </div>
      )}

      {/* Collapse toggle button - only visible when sidebar is expandable */}
      {canExpandSidebar && (
        <div className="border-t border-stroke-default p-3">
          <button
            type="button"
            onClick={toggleSidebar}
            className={`
              flex w-full items-center gap-3 rounded-lg px-3 py-2
              text-neutral-500 transition-colors duration-fast
              hover:bg-contrast-600 hover:text-neutral-50
              ${isCollapsed ? 'justify-center' : ''}
            `}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeftIcon className="h-5 w-5" />
                <span className="text-label-sm text-neutral-400 font-medium">
                  Collapse
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default Sidebar;
